import { SeededRNG } from '../util/rng.js';
import { ChunkLibrary } from './ChunkLibrary.js';

/**
 * Selects `chunkCount` chunks from a level's chunk pool using a seeded RNG
 * (reproducible per level seed) and concatenates them left-to-right into a
 * single tile grid + world-space spawn lists. Boss arenas / key rooms are
 * hand-placed by appending `bossChunk` after the procedural sequence rather
 * than being drawn from the pool, per spec.
 */
export function stitchLevel(levelDef) {
  const rng = new SeededRNG(levelDef.seed);
  const pool = ChunkLibrary.getPool(levelDef.district, levelDef.chunkPool);
  const height = pool[0].height;
  const tileSize = pool[0].tileSize;

  for (const chunk of pool) {
    if (chunk.height !== height || chunk.tileSize !== tileSize) {
      throw new Error(`[ChunkStitcher] Chunk ${chunk.id} has mismatched height/tileSize for its pool`);
    }
  }

  const sequence = [];
  let lastId = null;
  for (let i = 0; i < levelDef.chunkCount; i++) {
    let choice;
    let guard = 0;
    do {
      choice = rng.pick(pool);
      guard++;
    } while (pool.length > 1 && choice.id === lastId && guard < 8);
    sequence.push(choice);
    lastId = choice.id;
  }

  let bossChunk = null;
  if (levelDef.bossChunk) {
    bossChunk = ChunkLibrary.get(levelDef.district, levelDef.bossChunk);
    sequence.push(bossChunk);
  }

  const rows = Array.from({ length: height }, () => '');
  const enemies = [];
  const pickups = [];
  const hazards = [];
  let boss = null;
  let colOffset = 0;

  for (const chunk of sequence) {
    for (let r = 0; r < height; r++) {
      rows[r] += chunk.rows[r];
    }
    for (const e of chunk.spawns?.enemies ?? []) {
      enemies.push({ type: e.type, col: e.col + colOffset, row: e.row });
    }
    for (const p of chunk.spawns?.pickups ?? []) {
      pickups.push({ type: p.type, col: p.col + colOffset, row: p.row });
    }
    for (const h of chunk.hazards ?? []) {
      hazards.push({ type: h.type, col: h.col + colOffset, row: h.row, length: h.length });
    }
    if (chunk.boss) {
      boss = { type: chunk.boss.type, col: chunk.boss.col + colOffset, row: chunk.boss.row };
    }
    colOffset += chunk.width;
  }

  const totalCols = colOffset;
  const toWorld = (col, row) => ({ x: col * tileSize + tileSize / 2, y: row * tileSize + tileSize / 2 });
  const groundRow = height - 3;

  return {
    tileSize,
    widthTiles: totalCols,
    heightTiles: height,
    widthPx: totalCols * tileSize,
    heightPx: height * tileSize,
    rows,
    enemies: enemies.map((e) => ({ type: e.type, ...toWorld(e.col, e.row) })),
    pickups: pickups.map((p) => ({ type: p.type, ...toWorld(p.col, p.row) })),
    hazards: hazards.map((h) => ({ type: h.type, ...toWorld(h.col, h.row), length: h.length, tileSize })),
    boss: boss ? { type: boss.type, ...toWorld(boss.col, boss.row) } : null,
    playerStart: toWorld(1, groundRow),
    exitPoint: toWorld(totalCols - 2, groundRow)
  };
}

// Endless mode is two back-to-back procedural phases — Lower Sector's
// simpler roster (corp-soldier/drone-basic) first, then Upper Sector's
// harder one (adds turret/drone-elite/corp-soldier-heavy), the same order
// the campaign itself escalates in — rather than a flat, unchanging
// difficulty for however long a run lasts. Reuses stitchLevel() unchanged
// for each phase, then glues the two results together (offsetting the
// second phase's world-space content by the first phase's width) instead
// of teaching the stitcher itself a new code path.
const ENDLESS_LOWER_CHUNKS = 20;
const ENDLESS_UPPER_CHUNKS = 40;

/** Builds one endless run with a fresh random seed — no two attempts are the same layout. */
export function stitchEndlessLevel() {
  const seed = `endless-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

  const lower = stitchLevel({
    district: 'lower-sector',
    seed: `${seed}-lower`,
    chunkPool: ['street-01', 'street-02', 'alley-01'],
    chunkCount: ENDLESS_LOWER_CHUNKS
  });
  const upper = stitchLevel({
    district: 'upper-sector',
    seed: `${seed}-upper`,
    chunkPool: ['rooftop-01', 'rooftop-02', 'tower-interior-01'],
    chunkCount: ENDLESS_UPPER_CHUNKS
  });

  if (lower.tileSize !== upper.tileSize || lower.heightTiles !== upper.heightTiles) {
    throw new Error('[ChunkStitcher] Endless mode phases have mismatched grid size');
  }

  const offsetX = lower.widthPx;
  const shiftX = (list) => list.map((item) => ({ ...item, x: item.x + offsetX }));

  return {
    tileSize: lower.tileSize,
    widthTiles: lower.widthTiles + upper.widthTiles,
    heightTiles: lower.heightTiles,
    widthPx: lower.widthPx + upper.widthPx,
    heightPx: lower.heightPx,
    rows: lower.rows.map((row, r) => row + upper.rows[r]),
    enemies: [...lower.enemies, ...shiftX(upper.enemies)],
    pickups: [...lower.pickups, ...shiftX(upper.pickups)],
    hazards: [...lower.hazards, ...shiftX(upper.hazards)],
    boss: null,
    playerStart: lower.playerStart
  };
}
